import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '@/components/common/Toast';
import { OrderTypeConflictModal } from '@/components/common/OrderTypeConflictModal';
import { ProductInfo } from '@/components/store/ProductInfo';
import { useProductDetail } from '@/hooks/useProductDetail';
import { SEO } from '@/components/seo/SEO';
import { generateProductSEO } from '@/utils/seo';
import type { OrderType } from './product-detail.types';
import type { RestaurantWithProduct } from '@/types';

export function ProductDetailPage() {
  const { storeId, productId } = useParams<{ storeId: string; productId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { ToastContainer } = useToast();
  
  // 쿼리 파라미터에서 주문유형 읽기
  const queryParams = new URLSearchParams(location.search);
  const orderType = (queryParams.get('orderType') as OrderType) || 'DEADLINE_DISCOUNT';

  // 커스텀 훅으로 모든 상태와 로직 관리
  const {
    product,
    restaurant,
    isLoading,
    quantity,
    isAddingToCart,
    showOrderTypeModal,
    pendingOrderType,
    availabilityStatus,
    setQuantity,
    handleAddToCart,
    handleBuyNow,
    handleOrderTypeConfirm,
    handleOrderTypeCancel
  } = useProductDetail({
    storeId,
    productId,
    orderType
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-gray-600">상품 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">상품 정보를 찾을 수 없습니다</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
          >
            돌아가기
          </button>
        </div>
      </div>
    );
  }

  // 동적 SEO 생성
  const seoProps = (product && restaurant) ? generateProductSEO(product, {
    ...restaurant,
    pickup_instructions: restaurant.pickup_instructions || '픽업 정보 없음'
  } as RestaurantWithProduct) : null;

  return (
    <>
      {seoProps && <SEO {...seoProps} />}
      <div className="min-h-screen bg-white">
      {/* 헤더 */}
      <div className="sticky top-0 bg-white z-10 border-b border-gray-200">
        <div className="flex items-center justify-center px-4 py-3 relative">
          <button
            onClick={() => navigate(-1)}
            className="absolute left-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <img src="/icons/icon_back.svg" alt="뒤로 가기" width="24" height="24" />
          </button>
          <h1 className="text-lg font-semibold">상품 상세</h1>
        </div>
      </div>

      {/* 상품 정보 */}
      <ProductInfo
        product={product}
        quantity={quantity}
        availabilityStatus={availabilityStatus}
        onQuantityChange={setQuantity}
      />

      {/* 하단 버튼 */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
        <div className="flex gap-3">
          <button
            onClick={handleAddToCart}
            disabled={!availabilityStatus.isAvailable || isAddingToCart}
            className={`
              flex-1 py-3 border rounded-lg font-medium transition-colors
              ${!availabilityStatus.isAvailable
                ? 'border-gray-300 text-gray-500 bg-gray-100 cursor-not-allowed'
                : 'border-green-500 text-green-500 hover:bg-green-50'
              }
            `}
          >
            {!availabilityStatus.isAvailable 
              ? availabilityStatus.text 
              : isAddingToCart ? '담는 중...' : '장바구니'
            }
          </button>
          <button
            onClick={handleBuyNow}
            disabled={!availabilityStatus.isAvailable}
            className={`
              flex-1 py-3 rounded-lg font-medium transition-colors
              ${!availabilityStatus.isAvailable
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-green-500 text-white hover:bg-green-600'
              }
            `}
          >
            {!availabilityStatus.isAvailable 
              ? availabilityStatus.text 
              : '바로 주문'
            }
          </button>
        </div>
      </div>
      
      {/* 하단 버튼 공간 확보 */}
      <div className="h-20"></div>
      
      {/* 주문유형 충돌 모달 */}
      {pendingOrderType && (
        <OrderTypeConflictModal
          isOpen={showOrderTypeModal}
          currentOrderType={pendingOrderType.currentType}
          newOrderType={pendingOrderType.newType}
          onConfirm={handleOrderTypeConfirm}
          onCancel={handleOrderTypeCancel}
        />
      )}
      
      <ToastContainer />
      </div>
    </>
  );
}